import React, { useCallback } from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { DefaultDialogProps } from 'src/Libs/modals'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { Box, Grid, Stack } from '@mui/material'
import ToggleWriteAccessControl from 'src/Libs/installation-rights-management/components/user-access-and-invitations/ToggleWriteAccessControl'
import { Formik } from 'formik'
import { useAPIAccessChecker, useUserAccessAndInvitations } from 'src/Libs/installation-rights-management/hooks'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { getDisplayName, getAccountLinkStatus } from 'src/Libs/installation-rights-management/utils'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { ApiPermissionType } from 'src/Libs/api/types'
import { Checkbox } from 'src/Libs/inputs/formik'

export interface InstallationAccessSettingsDialogProps extends DefaultDialogProps {
  value: AccountLinkModel
  installation: Installation
  afterDataUpdate: () => void
}

const HAS_WRITE_ACCESS = 'hasWriteAccess'
const HAS_API_ACCESS = 'hasHagerEnergyApiAccess'

/**
 * Shows Table information in Mobile view
 */
const InstallationAccessSettingsDialog: React.VFC<InstallationAccessSettingsDialogProps> = ({
  place,
  onClose: onDialogClose,
  afterDataUpdate,
  value,
  installation,
}) => {
  const { t } = useCustomTranslation()
  const { toggleWriteAccess } = useUserAccessAndInvitations()
  const { snackError } = useSnackbar()

  const isAccessBlocked = value?.permissionType === ApiPermissionType.blocked
  const isAccessPending = value?.permissionType === ApiPermissionType.pending
  const isAccessPendingOrBlocked = isAccessPending || isAccessBlocked

  const accountLinkStatus = getAccountLinkStatus(value.permissionType, t)
  const { isUserLevelAllowedToGetApiAccess } = useAPIAccessChecker(installation)

  const formData = {
    hasWriteAccess: value.hasWriteAccess,
    hasHagerEnergyApiAccess: value.hasHagerEnergyApiAccess,
  } as AccountLinkModel

  const handleSubmit = useCallback(
    (values: AccountLinkModel) => {
      toggleWriteAccess(value, values.hasWriteAccess, values.hasHagerEnergyApiAccess)
        .then(() => {
          afterDataUpdate()
          onDialogClose()
        })
        .catch(snackError)
    },
    [snackError, toggleWriteAccess, value, afterDataUpdate, onDialogClose]
  )
  const accountDisplayName = getDisplayName(value)

  return (
    <Formik initialValues={formData} onSubmit={handleSubmit} enableReinitialize>
      {({ isValid, dirty, handleSubmit }) => {
        return (
          <Dialog
            title={t('installation.access-and-invitations.dialogs.access-settings.title', { name: accountDisplayName })}
            onClose={onDialogClose}
            place={place}
            maxWidth="sm"
            actions={{
              primary: {
                name: t('common.save'),
                disabled: !isValid || !dirty,
                onClick: handleSubmit,
              },
              secondary: [
                {
                  name: t('common.cancel'),
                },
              ],
            }}
          >
            <Box sx={{ py: 1 }}>
              <Grid container>
                <Grid item xs={12}>
                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Box>{t('installation.access-and-invitations.table.header.columns.type')}</Box>
                    <Box>{t(`common.accountType.${value.accountType}`)}</Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Box>{t('installation.access-and-invitations.table.header.columns.status')}</Box>
                    <Box>{accountLinkStatus}</Box>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Box>{t('installation.access-and-invitations.table.header.columns.can-read')}</Box>
                    {isAccessPendingOrBlocked ? null : <Checkbox disabled checked />}
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                    <Box>{t('installation.access-and-invitations.table.header.columns.can-write')}</Box>
                    {!isAccessPendingOrBlocked && <ToggleWriteAccessControl item={value} installation={installation} name={HAS_WRITE_ACCESS} />}
                  </Stack>
                </Grid>
                {isUserLevelAllowedToGetApiAccess(value) && (
                  <Grid item xs={12}>
                    <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                      <Box>{t('installation.access-and-invitations.table.header.columns.api-access')}</Box>
                      {!isAccessPendingOrBlocked && <ToggleWriteAccessControl item={value} installation={installation} name={HAS_API_ACCESS} />}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default InstallationAccessSettingsDialog
